// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/EnterpriseImpact.test.tsx
import { CaretDown, Database } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import cx from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MetricBlockCondensed } from "./canvas/blocks/MetricBlockCondensed";
import {
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_HOVER_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./statusPanelTokens";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";
import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
  chatAtomAgentInstanceId,
} from "../agentComponents/chatAtomRegistry";

export type EnterpriseImpactMetric = {
  id: string;
  label: string;
  value: string;
};

export type EnterpriseImpactProps = {
  metrics: EnterpriseImpactMetric[];
  /** One phrase for the whole cluster. Empty lets the headline be chosen from the metrics. */
  title?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  testIdPrefix?: string;
};

// why: a stored atom often has no title, and joining the labels restates the tiles under the toggle
const KNOWLEDGE_KIND = /\b(claims?|brands?|substantiation|packs?|findings)\b/i;

function isKnowledgeCluster(labels: string[]): boolean {
  if (labels.length < 2) return false;
  const hits = labels.filter((label) => KNOWLEDGE_KIND.test(label)).length;
  return hits >= 2 && hits * 2 >= labels.length;
}

function impactHeadline(
  metrics: EnterpriseImpactMetric[],
  title: string | undefined,
  knowledgeTitle: string,
  fallback: string,
): string {
  const provided = title?.trim() ?? "";
  if (provided.length > 0) return provided;
  const labels = metrics
    .map((metric) => metric.label.trim())
    .filter((label) => label.length > 0);
  if (labels.length === 1) return labels[0];
  if (isKnowledgeCluster(labels)) return knowledgeTitle;
  return fallback;
}

/**
 * Collapsible greeting-stat cluster. Figma EnterpriseImpact 49613:438 —
 * AgentSteps header only (transparent fill; open shell strokes gray-3) +
 * MetricBlockCondensed trio.
 */
export function EnterpriseImpact({
  metrics,
  title,
  open: openProp,
  onOpenChange,
  testIdPrefix = "enterprise-impact",
}: EnterpriseImpactProps) {
  const { t } = useTranslation("ui");
  const headline = impactHeadline(
    metrics,
    title,
    t("enterpriseImpact.knowledgeTitle"),
    t("enterpriseImpact.title"),
  );
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp != null;
  const expanded = isControlled ? openProp : uncontrolledOpen;

  const setExpanded = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.enterpriseImpact)}
      data-testid={testIdPrefix}
      // why: same muted accordion shell as AgentSteps / FeedMemoryCard — rounded-xl clips the padded hover fill.
      className={cx(
        "overflow-hidden bg-transparent",
        interactiveSurfaceClasses({
          expandable: true,
          muted: true,
          open: expanded,
        }),
      )}
    >
      <button
        type="button"
        data-testid={`${testIdPrefix}-toggle`}
        aria-expanded={expanded}
        aria-label={
          expanded
            ? t("enterpriseImpact.collapseAriaLabel")
            : t("enterpriseImpact.expandAriaLabel")
        }
        onClick={() => setExpanded(!expanded)}
        className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      >
        <Database
          size={16}
          className="shrink-0 text-puntt-neutral-gray-12"
          aria-hidden
        />
        <Text size="1" className="min-w-0 text-puntt-neutral-gray-12">
          {headline}
        </Text>
        <CaretDown
          size={14}
          aria-hidden
          className={`ml-auto shrink-0 text-puntt-neutral-gray-12 transition-transform duration-200 ${expanded ? "rotate-0" : "-rotate-90"}`}
        />
      </button>
      {expanded ? (
        <div
          data-testid={`${testIdPrefix}-items`}
          className={`${STATUS_PANEL_ITEMS_MUTED_CLASS} flex flex-col gap-2.5 sm:flex-row sm:flex-wrap`}
        >
          {metrics.map((metric) => (
            <div
              key={metric.id}
              className="w-full min-w-0 flex-1 basis-0 sm:w-auto"
            >
              <MetricBlockCondensed
                className="size-full"
                data-testid={`${testIdPrefix}-metric-${metric.id}`}
                agentComponentId={chatAtomAgentInstanceId(
                  CHAT_ATOM_AGENT_COMPONENT_IDS.metricBlockCondensed,
                  metric.id,
                )}
                label={metric.label}
                value={metric.value}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
